import { useState } from "react";
import { ExternalLink, Loader2, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { isStripeTestMode } from "@/config/stripePlans";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import stripeLogo from "@/assets/admin_dashboard/stripe_logo.png";

// IntegrationResult reads this key when Stripe sends the owner back.
export const PENDING_STRIPE_ACCOUNT_KEY = "pendingStripeAccountId";

type StripeAction = "connect" | "update" | "switch" | "dashboard";

const readPendingAccountId = () => {
  try {
    return localStorage.getItem(PENDING_STRIPE_ACCOUNT_KEY);
  } catch {
    return null;
  }
};

interface StripePaymentsRowProps {
  stripeConnectId: string | null | undefined;
}

// Sits under Dibs Bookings: storefront customers can only pay online once
// the business has a Stripe Express account linked (stripe_connect_id).
const StripePaymentsRow = ({ stripeConnectId }: StripePaymentsRowProps) => {
  const [loading, setLoading] = useState<StripeAction | null>(null);
  const isConnected = !!stripeConnectId;
  const hasPendingSetup = !isConnected && !!readPendingAccountId();

  const startOnboarding = async (action: Exclude<StripeAction, "dashboard">) => {
    setLoading(action);
    const pendingId = action === "switch" ? null : readPendingAccountId();
    try {
      const { data, error } = await supabase.functions.invoke("stripe-connect-onboard", {
        body: { pendingAccountId: pendingId, forceNew: action === "switch", test_mode: isStripeTestMode },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.url) throw new Error("Stripe didn't return a setup link");
      if (data.accountId) localStorage.setItem(PENDING_STRIPE_ACCOUNT_KEY, data.accountId);
      // Leave the spinner on while the browser heads to Stripe.
      window.location.href = data.url;
    } catch (err: any) {
      // A stale pending account (another business, or the other Stripe mode)
      // would fail the same way on every retry, so start fresh next time.
      if (pendingId) localStorage.removeItem(PENDING_STRIPE_ACCOUNT_KEY);
      console.error("Stripe Connect error:", err);
      toast.error(err?.message || "Couldn't start Stripe setup");
      setLoading(null);
    }
  };

  const openDashboard = async () => {
    setLoading("dashboard");
    // Open the tab now: browsers block tabs opened after an await.
    const stripeTab = window.open("", "_blank");
    try {
      const { data, error } = await supabase.functions.invoke("stripe-connect-onboard", {
        body: { action: "dashboard", test_mode: isStripeTestMode },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.url) throw new Error("Stripe didn't return a dashboard link");
      if (stripeTab) {
        stripeTab.opener = null;
        stripeTab.location.href = data.url;
      } else {
        window.location.href = data.url;
      }
    } catch (err: any) {
      stripeTab?.close();
      console.error("Stripe dashboard error:", err);
      toast.error(err?.message || "Couldn't open Stripe");
    } finally {
      setLoading(null);
    }
  };

  const description = isConnected
    ? "Customers can pay online when they book. Payouts go to your Stripe account."
    : hasPendingSetup
      ? "You started Stripe setup but didn't finish. Pick up where you left off."
      : "Connect Stripe so customers can pay online when they book.";

  return (
    <div className="flex items-center justify-between gap-4 py-2.5 sm:pl-16" data-testid="stripe-payments-row">
      <div className="flex min-w-0 items-center gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-muted p-2.5">
          <img src={stripeLogo} alt="" className="h-full w-full object-contain" />
        </div>
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">Online payments</span>
            {isConnected && (
              <Badge className="border-transparent bg-green-600 text-white hover:bg-green-600">Connected</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {isConnected ? (
          <>
            <Button
              variant="outline"
              size="sm"
              className="neutral-action-button"
              onClick={openDashboard}
              disabled={!!loading}
              aria-label="Open Stripe"
            >
              {loading === "dashboard" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ExternalLink className="h-3.5 w-3.5" />
              )}
              <span className="hidden sm:inline">Open Stripe</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-muted"
                  aria-label="More Stripe options"
                  disabled={!!loading}
                >
                  {loading === "update" || loading === "switch" ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => startOnboarding("update")}>
                  Update Stripe details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => startOnboarding("switch")}>
                  Connect a different Stripe account
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <Button size="sm" onClick={() => startOnboarding("connect")} disabled={!!loading}>
            {loading === "connect" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {hasPendingSetup ? "Finish Stripe setup" : "Connect Stripe"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default StripePaymentsRow;
